/* eslint sort-keys: "error" */
/* eslint-disable max-lines */
import { messages } from 'src/i18n/i18n';
import { v4 as uuidv4 } from 'uuid';
import { Component } from 'vue';
import macroTypeMap from '@redviking/argonaut-core-ui/src/applet/design/macros/macroTypeMap';
import { accessor } from 'src/store/store';
import { validateComparisons } from '@redviking/argonaut-core-ui/src/mes/lib/validate-comparison';
import { gatherComparisonInputs } from '@redviking/argonaut-util/src/mes/gather-comparison-inputs';
import { AppletDesignMenuItem } from '@redviking/argonaut-core-ui/src/applet/design/util/AppletDesignMenuItem';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { gqlClient } from 'src/util/gql-client';
import { GetWorkInstructionVersionDetailDocument } from 'types/db';
import { ArgoWorkInstructionVersionStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/work_instruction_version_status';
import { getAllAppletVariables } from '@redviking/argonaut-util/src/mes/appletUtil';
import type { ValidationResult } from 'types';

type PartialAttrs<T> = T extends object ? { [P in keyof T]?: Partial<T[P]> } : T;

export interface CellTypeMapItem<C extends Latest.Screen.Cells.AppletCell = Latest.Screen.Cells.AppletCell> extends AppletDesignMenuItem<C['type']> {
    /**
     * boolean to denote if the cell is a cell that is actually shown on the screen. If not the cell will be excluded from certain checks like screen warning dialogs
     */
    incorporeal?: boolean | ((cellCfg: Latest.Screen.Cells.AppletCell) => boolean);
    /**
     * Creates a default cell of this type. Can receive a partial cell configuration for overriding defaults
     */
    defaultCell?: (cfg?: PartialAttrs<C>) => C;
    validator: null | ((cellCfg: Latest.Screen.Cells.AppletCell, appletDesignVersionConfig: Latest.AppletDesignVersionConfig) => ValidationResult | Promise<ValidationResult>)
    /**
     * The component for configuration, when the item is selected
     */
    tab?: () => Promise<Component>;
    /**
     * the component for the treeview item
     * if not specified, the generic tree item will be used
     */
    tree?: () => Promise<Component>;
    /**
     * allows cell types to customize their delete behavior.
     * this function will run just before the cell is actually deleted.
     * Type is a generic cell type in order to decrease the complexity of the function signature. Cell type checking should be handled in the delete function.
     */
    delete?: (cellCfg: Latest.Screen.Cells.AppletCell) => void;
    icon?: string | ((cellCfg: Latest.Screen.Cells.AppletCell) => string);
    /**
     * Returns the cell attribute that can be used to uniquely distinguish the cell instance, this is the 'thing' that makes the cell unique (i.e. label for button, or schema name for attribute lookup)
     * Should not return the name of a variable the cell interfaces with, that is handled elsewhere
     * NOTE: The reason this is handled elsewhere is to ensure the correct order of precedence is applied
     */
    distinguishableIdentifier: ((cfg: Latest.Screen.Cells.AppletCell) => keyof C['attrs'] | null) | null;
    /**
     * Returns the variable or node id(s) this cell interfaces with, the item name will be fetched using the returned item id to formulate the cell subtitle
     */
    utilizedItemIds: ((cfg: Latest.Screen.Cells.AppletCell) => { source: 'variable' | 'node'; itemId: string; }[] | null) | null;
}

export type CellTypeMap = {
    [cellTypeKey in Latest.Screen.Cells.AppletCellType]: CellTypeMapItem<Latest.Screen.Cells.AppletCell & { type: cellTypeKey }>;
};

/**
 * defines how cells are configured
 */
export const cellTypeMap: CellTypeMap = {
    attrLookup: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.AttrLookup.Cell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    attrSchemaId: cfg?.attrs?.attrSchemaId || '',
                    attrSchemaName: cfg?.attrs?.attrSchemaName || '',
                    cols: cfg?.attrs?.cols || [],
                    conds: cfg?.attrs?.conds || [
                        {
                            attrSchemaPropId: '',
                            constOperand: '',
                            not: false,
                            type: 'propMatchStr',
                        },
                    ],
                    materialIdVarId: cfg?.attrs?.materialIdVarId || '',
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    showName: true,
                },
                id: uuidv4(),
                type: 'attrLookup',
            };
            return cell;
        },
        description: messages.mes.cells.attrLookup.description,
        disableAdd: () => !accessor.featureFlags.attributes,
        distinguishableIdentifier: c => {
            if (c.type !== 'attrLookup') {
                return null;
            }
            return 'attrSchemaName';
        },
        icon: 'mdi-table-search',
        tab: () => import('src/applet/design/cells/attr-lookup/AttrLookup.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.attrLookup.title,
        type: 'attrLookup',
        utilizedItemIds: c => {
            if (c.type !== 'attrLookup') {
                return null;
            }
            if (!c.attrs.materialIdVarId) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.materialIdVarId,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'attrLookup') {
                return {
                    errorMessage: `Unexpected cell type. Expected "attrLookup", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.attrSchemaId) {
                return {
                    errorMessage: messages.mes.cells.attrLookup.error.attrSchema,
                    status: 'error',
                };
            }
            if (cell.attrs.cols.length === 0) {
                return {
                    errorMessage: messages.mes.cells.attrLookup.error.cols,
                    status: 'error',
                };
            }
            if (!cell.attrs.materialIdVarId) {
                return {
                    errorMessage: messages.mes.cells.attrLookup.error.materialId,
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.materialIdVarId]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.materialIdVarId }),
                    status: 'error',
                };
            }
            for (const cond of cell.attrs.conds) {
                if ('attrSchemaPropId' in cond) {
                    if (!cond.attrSchemaPropId) {
                        return {
                            errorMessage: messages.mes.cells.attrLookup.error.propId,
                            status: 'error',
                        };
                    }
                }
                if (cond.source === 'var') {
                    const varOperand = cond.varOperand;
                    if (!varOperand) {
                        return {
                            errorMessage: messages.mes.cells.attrLookup.error.varNotSet,
                            status: 'error',
                        };
                    }
                    if (!allAppletVariables[varOperand]) {
                        return {
                            errorMessage: messages.mes.cells.attrLookup.error.missingVar(varOperand),
                            status: 'error',
                        };
                    }
                }
            }
            return {
                status: 'success',
            };
        },
    },
    btn: {
        defaultCell: cfg => {
            const buttonAttrs: Latest.Screen.Cells.Btn.Cell['attrs'] = {
                ...cfg?.attrs,
                appletEventId: cfg?.attrs?.appletEventId || null,
                borderRadius: cfg?.attrs?.borderRadius || '4px',
                color: cfg?.attrs?.color || '#21b8bb',
                enabled: cfg?.attrs?.enabled || 'true',
                fontColor: cfg?.attrs?.fontColor || '#000000',
                fontSize: cfg?.attrs?.fontSize || '14px',
                fontWeight: cfg?.attrs?.fontWeight || '400',
                label: cfg?.attrs?.label || 'Button',
                parentCellId: cfg?.attrs?.parentCellId || '',
                useItalics: cfg?.attrs?.useItalics || false,
                valign: cfg?.attrs?.valign || 'center',
                varBehavior: cfg?.attrs?.varBehavior || {
                    targetVarId: '',
                    type: 'timestamp',
                },
                variant: cfg?.attrs?.variant || 'default',
                visible: cfg?.attrs?.visible || true,
            };
            const cell: Latest.Screen.Cells.Btn.Cell = {
                ...cfg,
                attrs: buttonAttrs,
                id: uuidv4(),
                type: 'btn',
            };
            return cell;
        },
        description: messages.mes.cells.btn.description,
        distinguishableIdentifier: c => {
            if (c.type !== 'btn') {
                return null;
            }
            return 'label';
        },
        icon: 'mdi-gesture-tap-button',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Btn.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.btn.title,
        type: 'btn',
        utilizedItemIds: c => {
            if (c.type !== 'btn') {
                return null;
            }
            if (c.attrs.varBehavior.type === 'constant' || c.attrs.varBehavior.type === 'timestamp') {
                const varId = c.attrs.varBehavior.targetVarId;
                if (!varId) {
                    return null;
                }
                return [
                    {
                        itemId: varId,
                        source: 'variable',
                    },
                ];
            }
            return null;
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'btn') {
                return {
                    errorMessage: `Unexpected cell type. Expected "btn", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (cell.attrs.varBehavior.type === 'constant' || cell.attrs.varBehavior.type === 'timestamp') {
                if (!cell.attrs.varBehavior.targetVarId) {
                    return {
                        errorMessage: 'Target Variable required for Button',
                        status: 'error',
                    };
                }
                const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
                if (!allAppletVariables[cell.attrs.varBehavior.targetVarId]) {
                    return {
                        errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.varBehavior.targetVarId }),
                        status: 'error',
                    };
                }
            }
            if (cell.attrs.varBehavior.type === 'navToScreen' && !cell.attrs.targetScreenId) {
                return {
                    errorMessage: 'Target Screen required for Button with navigation behavior',
                    status: 'error',
                };
            }

            if (cell.attrs.varBehavior.type === 'script') {
                const script = appletDesignVersionConfig.scripts.find(s => s.id === cell.attrs.linkedScriptId);
                if (!script) {
                    return {
                        errorMessage: 'Script not found',
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
    circularProgress: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.CircularProgressCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    dialColor: cfg?.attrs?.dialColor || '#4CAF50FF',
                    innerText: cfg?.attrs?.innerText || '',
                    maxValue: cfg?.attrs?.maxValue || 100,
                    minValue: cfg?.attrs?.minValue || 0,
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    sourceVar: cfg?.attrs?.sourceVar || '',
                },
                id: uuidv4(),
                type: 'circularProgress',
            };
            return cell;
        },
        description: messages.mes.cells.circularProgress.description,
        distinguishableIdentifier: c => {
            if (c.type !== 'circularProgress') {
                return null;
            }
            return 'innerText';
        },
        icon: 'mdi-refresh',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/CircularProgress.tab.cell.vue').then(m => m.default),
        text: 'Circular Progress',
        type: 'circularProgress',
        utilizedItemIds: c => {
            if (c.type !== 'circularProgress') {
                return null;
            }
            if (!c.attrs.sourceVar) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.sourceVar,
                    source: 'variable',
                },
            ];
        },
        validator: (cfg, appletDesignVersionConfig) => {
            if (cfg.type !== 'circularProgress') {
                return {
                    errorMessage: `Unexpected cell type. Expected "circularProgress", got "${cfg.type}"`,
                    status: 'error',
                };
            }
            if (!cfg.attrs.sourceVar) {
                return {
                    errorMessage: 'Circular Progress variable is required',
                    status: 'error',
                };
            }
            if (cfg.attrs.minValue > cfg.attrs.maxValue) {
                return {
                    errorMessage: 'Min value must be less than max value',
                    status: 'error',
                };
            }
            if (cfg.attrs.flashingCondition) {
                const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
                const clearConditionInputs = gatherComparisonInputs(cfg.attrs.flashingCondition.comparisons);
                for (const appletVarId of clearConditionInputs) {
                    if (!allAppletVariables[appletVarId]) {
                        return {
                            errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cfg.name, cellType: cfg.type, varId: appletVarId }),
                            status: 'error',
                        };
                    }
                }
                return validateComparisons(cfg.attrs.flashingCondition.comparisons);
            }
            return {
                status: 'success',
            };
        },
    },
    datetime: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.DateTimeCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    format: cfg?.attrs?.format || 'datetime',
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    useItalics: false,
                } as Latest.Screen.Cells.DateTimeCell['attrs'],
                id: uuidv4(),
                type: 'datetime',
            };
            return cell;
        },
        description: messages.mes.cells.datetime.description,
        distinguishableIdentifier: null,
        icon: 'mdi-timetable',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/DateTime.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.datetime.title,
        type: 'datetime',
        utilizedItemIds: null,
        validator: null,
    },
    dialog: {
        disableAdd: true,
        distinguishableIdentifier: null,
        incorporeal: true,
        text: 'Dialog',
        type: 'dialog',
        utilizedItemIds: null,
        validator: null,
    },
    displayProcess: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.DisplayProcessCell.Cell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    processData: cfg?.attrs?.processData || {
                        kind: 'simple',
                        materialIdControlVariable: '',
                        processId: '',
                    },
                },
                id: uuidv4(),
                type: 'displayProcess',
            };
            return cell;
        },
        description: 'Display current process counts and status for a material',
        distinguishableIdentifier: null,
        icon: 'mdi-order-bool-ascending-variant',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/DisplayProcess.tab.cell.vue').then(m => m.default),
        text: 'Process Display',
        // treeText: (cell, entity) => {
        //     if (cell.type !== 'displayProcess') {
        //         return '';
        //     }
        //     if (cell.attrs.processData.kind === 'simple' && cell.attrs.processData.processCollectNodeId) {
        //         const f = Object.entries(entity.flows).find(([ _, v ]) => cell.attrs.processData.kind === 'simple' && v.nodes[cell.attrs.processData.processCollectNodeId] !== undefined);
        //         if (!f) {
        //             return '';
        //         }
        //         const linkedNode = f[1].nodes[cell.attrs.processData.processCollectNodeId];
        //         if (!linkedNode || linkedNode.kind !== 'processCollect') {
        //             return '';
        //         }
        //         return `${linkedNode.label || 'Unnamed Process Collect Node'} - ${cell.name || 'Unnamed Process Display Cell'}`;
        //     }
        //     return '';
        // },
        type: 'displayProcess',
        utilizedItemIds: c => {
            if (c.type !== 'displayProcess') {
                return null;
            }
            if (c.attrs.processData.kind === 'advanced') {
                return null;
            }
            if (!c.attrs.processData.materialIdControlVariable) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.processData.materialIdControlVariable,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'displayProcess') {
                return {
                    errorMessage: `Unexpected cell type. Expected "displayProcess", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (cell.attrs.processData.kind === 'simple') {
                if (!cell.attrs.processData.materialIdControlVariable) {
                    return {
                        errorMessage: 'A variable to provide the material id is required for the display process cell',
                        status: 'error',
                    };
                }
                const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
                if (!allAppletVariables[cell.attrs.processData.materialIdControlVariable]) {
                    return {
                        errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.processData.materialIdControlVariable }),
                        status: 'error',
                    };
                }
                if (!cell.attrs.processData.processId) {
                    return {
                        errorMessage: 'A process must be selected for the display process cell',
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
    gauge: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.GaugeCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    baseColor: cfg?.attrs?.baseColor || '#4CAF50FF',
                    boundingMode: cfg?.attrs?.boundingMode || 'percent',
                    colorMode: cfg?.attrs?.colorMode || 'thresholds',
                    maxValue: cfg?.attrs?.maxValue || 100,
                    minValue: cfg?.attrs?.minValue || 0,
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    roundedBars: cfg?.attrs?.roundedBars ?? false,
                    sourceVar: cfg?.attrs?.sourceVar || '',
                    thresholds: cfg?.attrs?.thresholds || [],
                    variant: cfg?.attrs?.variant || 'speedometer',
                },
                id: uuidv4(),
                type: 'gauge',
            };
            return cell;
        },
        description: messages.mes.cells.gauge.description,
        distinguishableIdentifier: null,
        icon: 'mdi-gauge',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/gauge/Gauge.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.gauge.title,
        type: 'gauge',
        utilizedItemIds: c => {
            if (c.type !== 'gauge') {
                return null;
            }
            if (!c.attrs.sourceVar) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.sourceVar,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'gauge') {
                return {
                    errorMessage: `Unexpected cell type. Expected "gauge", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.sourceVar) {
                return {
                    errorMessage: 'Gauge source variable is required',
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.sourceVar]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.sourceVar }),
                    status: 'error',
                };
            }
            if (cell.attrs.boundingMode === 'range') {
                if (cell.attrs.minValue === undefined || cell.attrs.maxValue === undefined) {
                    return {
                        errorMessage: 'Min and Max values are required when bounding mode is "range"',
                        status: 'error',
                    };
                }
                if (cell.attrs.minValue >= cell.attrs.maxValue) {
                    return {
                        errorMessage: 'Min value must be less than max value',
                        status: 'error',
                    };
                }
            }
            if (cell.attrs.colorMode === 'var') {
                if (!cell.attrs.dialColorVar) {
                    return {
                        errorMessage: 'Gauge color variable is required when color mode is "var"',
                        status: 'error',
                    };
                }
                if (!allAppletVariables[cell.attrs.dialColorVar]) {
                    return {
                        errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.dialColorVar }),
                        status: 'error',
                    };
                }
            }
            if (cell.attrs.thresholds) {
                if (cell.attrs.thresholds.length > 10) {
                    return {
                        errorMessage: 'Gauge supports a maximum of 10 color thresholds',
                        status: 'error',
                    };
                }
                for (const threshold of cell.attrs.thresholds) {
                    if (cell.attrs.boundingMode === 'percent' && (threshold.value < 0 || threshold.value > 100)) {
                        return {
                            errorMessage: 'Threshold percentage must be between 0 and 100',
                            status: 'error',
                        };
                    } else if (cell.attrs.boundingMode === 'range' && (threshold.value < cell.attrs.minValue! || threshold.value > cell.attrs.maxValue!)) {
                        return {
                            errorMessage: 'Threshold value must be between min and max values',
                            status: 'error',
                        };
                    }
                }
            }
            if (cell.attrs.label?.type === 'var') {
                if (!allAppletVariables[cell.attrs.label.varId]) {
                    return {
                        errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.label.varId }),
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
    input: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.InputCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    appletEventId: cfg?.attrs?.appletEventId || null,
                    captureFocus: cfg?.attrs?.captureFocus || false,
                    enabled: cfg?.attrs?.enabled || 1,
                    errorMsg: cfg?.attrs?.errorMsg || '',
                    label: cfg?.attrs?.label || 'Input',
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    trim: cfg?.attrs?.trim || true,
                    writeVarId: cfg?.attrs?.writeVarId || '',
                },
                id: uuidv4(),
                type: 'input',
            };
            return cell;
        },
        description: messages.mes.cells.input.description,
        distinguishableIdentifier: c => {
            if (c.type !== 'input') {
                return null;
            }
            return 'label';
        },
        icon: 'mdi-form-textbox',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Input.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.input.title,
        type: 'input',
        utilizedItemIds: c => {
            if (c.type !== 'input') {
                return null;
            }
            if (!c.attrs.writeVarId) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.writeVarId,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'input') {
                return {
                    errorMessage: `Unexpected cell type. Expected "input", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.writeVarId) {
                return {
                    errorMessage: 'Target Variable required for Input',
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.writeVarId]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.writeVarId }),
                    status: 'error',
                };
            }
            if (cell.attrs.clearCondition) {
                if (cell.attrs.clearCondition.evalTrigger === 'varChange') {
                    const clearConditionInputs = gatherComparisonInputs(cell.attrs.clearCondition.comparisons);
                    for (const appletVarId of clearConditionInputs) {
                        if (!allAppletVariables[appletVarId]) {
                            return {
                                errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: appletVarId }),
                                status: 'error',
                            };
                        }
                    }
                    return validateComparisons(cell.attrs.clearCondition.comparisons);
                } else if (cell.attrs.clearCondition.evalTrigger === 'event') {
                    if (!cell.attrs.clearCondition.eventTrigger) {
                        return {
                            errorMessage: 'Input cell event trigger needs an event',
                            status: 'error',
                        };
                    }
                }
            }
            return {
                status: 'success',
            };
        },
    },
    macroTarget: {
        delete (cfg) {
            if (cfg.type !== 'macroTarget') {
                return;
            }
            accessor.appletDesign.removeMacroCfg({
                macroId: cfg.attrs.macroId,
            });
        },
        disableAdd: true,
        distinguishableIdentifier: null,
        icon: cfg => {
            if (cfg.type !== 'macroTarget') {
                return '';
            }
            return macroTypeMap[cfg.attrs.macroType].icon;
        },
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/macroTarget/MacroTarget.tab.cell.vue').then(m => m.default),
        text: '',
        tree: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/macroTarget/MacroTarget.tree.cell.vue').then(m => m.default),
        type: 'macroTarget',
        utilizedItemIds: null,
        validator: null,
    },
    markdown: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.MarkdownCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    content: cfg?.attrs?.content || '## Sample Markdown',
                    parentCellId: cfg?.attrs?.parentCellId || '',
                },
                id: uuidv4(),
                type: 'markdown',
            };
            return cell;
        },
        description: messages.mes.cells.markdown.description,
        distinguishableIdentifier: null,
        icon: 'mdi-language-markdown-outline',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Markdown.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.markdown.title,
        type: 'markdown',
        utilizedItemIds: c => {
            if (c.type !== 'markdown') {
                return null;
            }
            const contentVarId = c.modifiers?.content;
            if (!contentVarId) {
                return null;
            }
            return [
                {
                    itemId: contentVarId,
                    source: 'variable',
                },
            ];
        },
        validator: null,
    },
    materialLookup: {
        description: messages.mes.cells.materialLookup.description,
        disableAdd: true, // only used by macro
        distinguishableIdentifier: null,
        tab: () => import('src/applet/design/macros/material-lookup/MaterialLookup.tab.macro.vue').then(m => m.default),
        text: messages.mes.cells.materialLookup.title,
        type: 'materialLookup',
        utilizedItemIds: null,
        validator: null,
    },
    media: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.Media.Cell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    items: cfg?.attrs?.items || [],
                    parentCellId: cfg?.attrs?.parentCellId || '',
                },
                id: uuidv4(),
                type: 'media',
            };
            return cell;
        },
        description: messages.mes.cells.media.description,
        distinguishableIdentifier: null,
        icon: 'mdi-image',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Media.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.media.title,
        type: 'media',
        utilizedItemIds: c => {
            if (c.type !== 'media') {
                return null;
            }
            return c.attrs.items.filter(i => i.varId).map<{ source: 'variable' | 'node'; itemId: string; }>(i => ({
                itemId: i.varId!,
                source: 'variable',
            }));
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'media') {
                return {
                    errorMessage: `Unexpected cell type. Expected "media", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.items.length) {
                if (cell.attrs.parentCellId) {
                    const screen = appletDesignVersionConfig.screens.find(s => s.cells.find(c => c.id === cell.attrs.parentCellId));
                    if (!screen) {
                        return {
                            errorMessage: 'Media cell has a parent cell that cannot be found',
                            status: 'error',
                        };
                    }
                    const parentCell = screen.cells.find(c => c.id === cell.attrs.parentCellId);
                    if (!parentCell) {
                        return {
                            errorMessage: 'Media cell has a parent cell that cannot be found',
                            status: 'error',
                        };
                    }
                    if (parentCell.type !== 'workInstruction' || (parentCell.type === 'workInstruction' && parentCell.attrs.displayCfg.kind === 'simple')) {
                        return {
                            errorMessage: 'Media cell needs at least one piece of media',
                            status: 'error',
                        };
                    }
                } else {
                    return {
                        errorMessage: 'Media cell needs at least one piece of media',
                        status: 'error',
                    };
                }
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            for (const item of cell.attrs.items) {
                if (item.varId && !allAppletVariables[item.varId]) {
                    return {
                        errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: item.varId }),
                        status: 'error',
                    };
                }
                if (item.media.type === 'varForExternal') {
                    if (!item.media.varId) {
                        return {
                            errorMessage: 'Variable required for dynamic media',
                            status: 'error',
                        };
                    }
                    if (!allAppletVariables[item.media.varId]) {
                        return {
                            errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: item.media.varId }),
                            status: 'error',
                        };
                    }
                }
                if (typeof item.timeout === 'number' && item.timeout <= 0) {
                    return {
                        errorMessage: 'Timeout must be greater than 0',
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
    processList: {
        disableAdd: true,
        distinguishableIdentifier: null,
        icon: 'mdi-order-bool-ascending-variant',
        text: 'Process List',
        type: 'processList',
        utilizedItemIds: null,
        validator: null,
    },
    select: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.SelectCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    clearable: cfg?.attrs?.clearable ?? false,
                    enabled: cfg?.attrs?.enabled ?? 1,
                    label: cfg?.attrs?.label || 'Select',
                    options: cfg?.attrs?.options || [],
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    writeVarId: cfg?.attrs?.writeVarId || '',
                },
                id: uuidv4(),
                type: 'select',
            };
            return cell;
        },
        description: messages.mes.cells.select.description,
        distinguishableIdentifier: c => {
            if (c.type !== 'select') {
                return null;
            }
            return 'label';
        },
        icon: 'mdi-form-select',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Select.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.select.title,
        type: 'select',
        utilizedItemIds: c => {
            if (c.type !== 'select') {
                return null;
            }
            if (!c.attrs.writeVarId) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.writeVarId,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'select') {
                return {
                    errorMessage: `Unexpected cell type. Expected "select", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.writeVarId) {
                return {
                    errorMessage: 'Target Variable required for Select',
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.writeVarId]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.writeVarId }),
                    status: 'error',
                };
            }
            if (cell.attrs.options.length === 0) {
                return {
                    errorMessage: 'At least one option is required for Select',
                    status: 'error',
                };
            }
            return { status: 'success' };
        },
    },
    text: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.TextCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    content: cfg?.attrs?.content || 'sample text',
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    textColor: cfg?.attrs?.textColor || 'black',
                    useItalics: false,
                } as Latest.Screen.Cells.TextCell['attrs'],
                id: uuidv4(),
                type: 'text',
            };
            return cell;
        },
        description: messages.mes.cells.text.description,
        distinguishableIdentifier: null,
        icon: 'mdi-alphabetical-variant',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Text.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.text.title,
        type: 'text',
        utilizedItemIds: c => {
            if (c.type !== 'text') {
                return null;
            }
            const contentVarId = c.modifiers?.content;
            if (!contentVarId) {
                return null;
            }
            return [
                {
                    itemId: contentVarId,
                    source: 'variable',
                },
            ];
        },
        validator: null,
    },
    timer: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.TimerCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    dialColor: cfg?.attrs?.dialColor || '#4CAF50FF',
                    flashingThreshold: cfg?.attrs?.flashingThreshold || 10,
                    format: cfg?.attrs?.format || 'hh:mm:ss',
                    maxValue: cfg?.attrs?.maxValue || 180,
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    sourceVarId: cfg?.attrs?.sourceVarId || '',
                },
                id: uuidv4(),
                type: 'timer',
            };
            return cell;
        },
        description: messages.mes.cells.timer.description,
        distinguishableIdentifier: null,
        icon: 'mdi-timer',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/Timer.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.timer.title,
        type: 'timer',
        utilizedItemIds: c => {
            if (c.type !== 'timer') {
                return null;
            }
            if (!c.attrs.sourceVarId) {
                return null;
            }
            return [
                {
                    itemId: c.attrs.sourceVarId,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'timer') {
                return {
                    errorMessage: `Unexpected cell type. Expected "timer", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.sourceVarId) {
                return {
                    errorMessage: 'Timer variable is required',
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.sourceVarId]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.sourceVarId }),
                    status: 'error',
                };
            }
            return {
                status: 'success',
            };
        },
    },
    workInstruction: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.WorkInstructionCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    displayCfg: cfg?.attrs?.displayCfg || {
                        btnCfg: {
                            color: '#21b8bb',
                            label: 'Complete Step',
                        },
                        kind: 'simple',
                        style: 'default',
                    },
                    workInstructionName: cfg?.attrs?.workInstructionName || '',
                    workInstructionVersionId: cfg?.attrs?.workInstructionVersionId || '',
                },
                id: uuidv4(),
                type: 'workInstruction',
            };
            return cell;
        },
        description: messages.mes.cells.workInstruction.description,
        distinguishableIdentifier: c => {
            if (c.type !== 'workInstruction') {
                return null;
            }
            return 'workInstructionName';
        },
        icon: 'mdi-text-box-multiple-outline',
        incorporeal: c => {
            if (c.type !== 'workInstruction') {
                return false;
            }
            if (c.attrs.displayCfg.kind === 'advanced') {
                return true;
            }
            return false;
        },
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/work-instruction/WorkInstruction.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.workInstruction.title,
        tree: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/work-instruction/WorkInstruction.tree.cell.vue').then(m => m.default),
        type: 'workInstruction',
        utilizedItemIds: null,
        validator: async (cell) => {
            if (cell.type !== 'workInstruction') {
                return {
                    errorMessage: `Unexpected cell type. Expected "workInstruction", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (cell.attrs.workInstructionVersionId) {
                const {
                    argo_work_instruction_version_by_pk,
                } = await gqlClient.request({
                    document: GetWorkInstructionVersionDetailDocument,
                    variables: {
                        workInstructionVersionId: cell.attrs.workInstructionVersionId,
                    },
                });
                if (!argo_work_instruction_version_by_pk) {
                    return {
                        errorMessage: `Work instruction version with id: ${cell.attrs.workInstructionVersionId} not found.`,
                        status: 'error',
                    };
                }
                if (argo_work_instruction_version_by_pk.status !== ArgoWorkInstructionVersionStatusEnum.Released) {
                    if (argo_work_instruction_version_by_pk.status === ArgoWorkInstructionVersionStatusEnum.Retired) {
                        if (!argo_work_instruction_version_by_pk.discontinue_after) {
                            return {
                                errorMessage: `Work instruction version with id: ${cell.attrs.workInstructionVersionId} is retired but does not have a discontinue date.`,
                                status: 'error',
                            };
                        } else if (new Date() >= new Date(argo_work_instruction_version_by_pk.discontinue_after)) {
                            return {
                                errorMessage: `Work instruction version with id: ${cell.attrs.workInstructionVersionId} is retired and cannot be used.`,
                                status: 'error',
                            };
                        }
                    } else {
                        return {
                            errorMessage: `Work instruction version with id: ${cell.attrs.workInstructionVersionId} is not released and cannot be used.`,
                            status: 'error',
                        };
                    }
                }
            }
            return {
                status: 'success',
            };
        },
    },
    workInstructionProcessList: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.WorkInstructionProcessListCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    orientation: cfg?.attrs?.orientation || 'vertical',
                    workInstructionId: cfg?.attrs?.workInstructionId || '',
                },
                id: uuidv4(),
                type: 'workInstructionProcessList',
            };
            return cell;
        },
        disableAdd: true,
        distinguishableIdentifier: null,
        icon: 'mdi-order-bool-ascending-variant',
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/WorkInstructionProcessList.tab.cell.vue').then(m => m.default),
        text: 'Work Instruction Process List',
        type: 'workInstructionProcessList',
        utilizedItemIds: null,
        validator: null,
    },
    workOrderModal: {
        defaultCell: cfg => {
            const cell: Latest.Screen.Cells.WorkOrderModalCell = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    activateCondition: cfg?.attrs?.activateCondition || {
                        boolOperation: 'and',
                        comparisons: [],
                        evalTrigger: 'varChange',
                    },
                    parentCellId: cfg?.attrs?.parentCellId || '',
                    writeVarId: cfg?.attrs?.writeVarId || '',
                },
                id: uuidv4(),
                type: 'workOrderModal',
            };
            return cell;
        },
        description: messages.mes.cells.workOrderModal.description,
        disableAdd: () => !accessor.featureFlags.unstableWorkOrders,
        distinguishableIdentifier: null,
        icon: '$argo-work-order',
        incorporeal: true,
        tab: () => import('@redviking/argonaut-core-ui/src/applet/design/cells/WorkOrderModal.tab.cell.vue').then(m => m.default),
        text: messages.mes.cells.workOrderModal.title,
        type: 'workOrderModal',
        utilizedItemIds: c => {
            if (c.type !== 'workOrderModal') {
                return null;
            }
            if (!c.attrs.writeVarId) {
                return null;
            }
            return [
                    {
                    itemId: c.attrs.writeVarId,
                    source: 'variable',
                },
            ];
        },
        validator: (cell, appletDesignVersionConfig) => {
            if (cell.type !== 'workOrderModal') {
                return {
                    errorMessage: `Unexpected cell type. Expected "workOrderModal", got "${cell.type}"`,
                    status: 'error',
                };
            }
            if (!cell.attrs.writeVarId) {
                return {
                    errorMessage: 'Target Variable required for Input',
                    status: 'error',
                };
            }
            const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
            if (!allAppletVariables[cell.attrs.writeVarId]) {
                return {
                    errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: cell.attrs.writeVarId }),
                    status: 'error',
                };
            }

            if (cell.attrs.activateCondition.evalTrigger === 'varChange') {
                const clearConditionInputs = gatherComparisonInputs(cell.attrs.activateCondition.comparisons);
                for (const appletVarId of clearConditionInputs) {
                    if (!allAppletVariables[appletVarId]) {
                        return {
                            errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({ cellLabel: cell.name, cellType: cell.type, varId: appletVarId }),
                            status: 'error',
                        };
                    }
                }
                return validateComparisons(cell.attrs.activateCondition.comparisons, true);
            } else if (cell.attrs.activateCondition.evalTrigger === 'event') {
                if (!cell.attrs.activateCondition.eventTrigger) {
                    return {
                        errorMessage: 'Work order modal trigger needs an event',
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
};

export const cellTypeMapItems = Object.keys(cellTypeMap).map(k => cellTypeMap[k as Latest.Screen.Cells.AppletCellType]) as CellTypeMapItem[];

export default cellTypeMap;

if (import.meta.webpackHot) {
    import.meta.webpackHot.accept('@redviking/argonaut-core-ui/src/applet/design/macros/macroTypeMap');
    import.meta.webpackHot.accept('@redviking/argonaut-core-ui/src/i18n/i18n');
}
